import { useTranslation } from 'react-i18next';
import { RaadLogo } from '@/components/RaadLogo';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Shown when a Raad STAFF account holds a session on the customer origin (17i).
 *
 * A panel, NOT a redirect — Mahad's decision: the staff refresh cookie is
 * host-scoped, so bouncing to `admin.` would drop the session and cost a
 * re-auth, and it would make the tenant app unreachable to anyone holding a
 * staff token, which is the exact state that needs to be inspectable when
 * debugging this class. So the door stays open: sign-out is reachable, and the
 * guidance the login screen can no longer give (both doors refuse generically
 * server-side, no enumeration oracle) lives HERE, where the app already knows
 * who signed in — authenticated, in place.
 */

/**
 * The admin portal shares the apex domain on the `admin.` label
 * (`admin.{$RAAD_DOMAIN}` in the Caddyfile). Derive it from the current host so
 * `app.` and `beta.` both point at the right place; fall back to production if
 * the host has no subdomain (local dev).
 */
function adminPortalUrl(): string {
  if (typeof window === 'undefined') return 'https://admin.raadtelematics.com';
  const { protocol, host } = window.location;
  const parts = host.split('.');
  if (parts.length >= 3) {
    parts[0] = 'admin';
    return `${protocol}//${parts.join('.')}`;
  }
  return 'https://admin.raadtelematics.com';
}

export function StaffPanel() {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-raad-bg px-5 py-10 text-raad-text">
      <RaadLogo size={44} title={t('brand.name')} />
      <section className="w-full max-w-md rounded-panel border border-raad-border bg-raad-panel px-6 py-8 text-center">
        <h1 className="font-display text-lg font-bold">{t('staffPanel.title')}</h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-raad-muted">
          {t('staffPanel.body')}
        </p>
        <a
          href={adminPortalUrl()}
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-xl bg-raad-primary px-5 text-sm font-semibold text-raad-onprimary transition-colors hover:bg-raad-accent"
        >
          {t('staffPanel.adminLink')}
        </a>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => void signOut()}
            className="min-h-11 text-sm font-semibold text-raad-muted underline-offset-4 hover:text-raad-text hover:underline"
          >
            {t('staffPanel.signOut')}
          </button>
        </div>
      </section>
    </main>
  );
}
